import { FC } from 'react';
import { Typography, Stack, AccordionDetails, Button } from '@mui/material';
import { VyIcon, VyIconType } from '@vy/ui-design-system';
import { isInvoiceSyncToIntegrationAllowed } from '@vy/invoices';
import { INVOICE_REVIEW_STATUSES } from '@vy/types';
import { isNotEmptyString, Optional, ItemOf, raiseUnreachable, withDefault, now } from '@vy/utils';
import { formatDate } from '../../../../../../../../../../../../utils/date';
import { runMutation } from '../../../../../../../../../../../../utils/vy-mutation';
import { getQuickBooksInvoiceUrl } from '../../../../../../../../../../../../services/integration-invoice/quick-books-integration-invoice-link';
import { useQueryClient } from 'react-query';
import { GetLightInvoicesResponse, GetExportedIntegrationsResponse } from '@vy/api-definition';
import { SYNC_JOB_STATUSES, SyncJobStatus } from '@vy/integrations-types';
import { useQuickBooksExportInvoices } from '../../../../../../../../../../../../hooks/requests/integrations';
import { queryKeys } from '../../../../../../../../../../../../services/query-key/query-keys';

type QbIntegrationStatusAccordionDetailsProps = {
  quickbooksIntegration: Optional<ItemOf<GetExportedIntegrationsResponse>>;
  invoice: ItemOf<GetLightInvoicesResponse['invoices']>;
};

export const QbIntegrationStatusAccordionDetails: FC<QbIntegrationStatusAccordionDetailsProps> = ({
  quickbooksIntegration,
  invoice,
}) => {
  const exportToQuickBooksMutation = useQuickBooksExportInvoices();
  const queryClient = useQueryClient();

  const isApproved = invoice.reviewStatus === INVOICE_REVIEW_STATUSES.Approved;
  const isSyncable = isInvoiceSyncToIntegrationAllowed(invoice);
  const isSynced = quickbooksIntegration?.status === SYNC_JOB_STATUSES.COMPLETED;
  const status = withDefault(quickbooksIntegration?.status, SYNC_JOB_STATUSES.PENDING);
  const label = getLabel(status);
  const icon = getIcon(status);

  const getStatusText = () => {
    if (isSynced) {
      if (quickbooksIntegration?.updatedAt) {
        return formatDate(quickbooksIntegration.updatedAt);
      }
      return formatDate(now());
    }
    if (!isApproved) {
      return 'Approve invoice to enable syncing';
    }
    if (!isSyncable) {
      return 'Child invoices sync through the parent customer';
    }
    if (quickbooksIntegration?.updatedAt) {
      return formatDate(quickbooksIntegration.updatedAt);
    }
    return formatDate(now());
  };

  const handleSyncToQuickBooks = async () => {
    await runMutation(
      exportToQuickBooksMutation,
      { invoiceIds: [invoice.id] },
      {
        pending: 'Exporting to QuickBooks...',
        success: 'Exported to QuickBooks',
        error: 'Failed to export to QuickBooks',
      },
      {
        onSuccess: async () => {
          await Promise.all([
            queryClient.invalidateQueries(queryKeys.invoice(invoice.id)),
            queryClient.invalidateQueries(queryKeys.exportedInvoiceIntegrations(invoice.id)),
          ]);
        },
      },
    );
  };

  const handleOpenOnQuickBooks = () => {
    const externalId = quickbooksIntegration?.integrationExternalId;

    if (isNotEmptyString(externalId)) {
      const url = getQuickBooksInvoiceUrl(externalId);

      window.open(url, '_blank');
    }
  };

  return (
    <AccordionDetails sx={{ p: 0 }}>
      <Stack
        sx={{
          gap: 2,
          mb: 1.5,
          mt: 1.5
        }}>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            gap: "74px",
            flexShrink: 0,
            justifyContent: "space-between"
          }}>
          <Stack
            direction="column"
            sx={{
              alignItems: "flex-start",
              gap: "4px"
            }}>
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                gap: 1
              }}>
              <VyIcon icon={icon} strokeOpacity={0} />
              <Typography
                sx={{
                  fontSize: 14,
                  lineHeight: "20px"
                }}>
                {label}
              </Typography>
            </Stack>
            <Typography
              sx={{
                fontSize: 14,
                lineHeight: "20px",
                color: "#7E8796"
              }}>
              {getStatusText()}
            </Typography>
          </Stack>

          {!isSynced && (
            <Button
              variant="text"
              size="medium"
              startIcon={<VyIcon icon="Synchronize" transform="scale(0.7)" />}
              onClick={handleSyncToQuickBooks}
              disabled={!isSyncable}
            >
              Sync
            </Button>
          )}
        </Stack>

        <Button
          variant="contained"
          color="secondary"
          size="large"
          endIcon={<VyIcon icon="OpenNew" />}
          onClick={handleOpenOnQuickBooks}
          disabled={!isSynced}
        >
          Open on QuickBooks
        </Button>
      </Stack>
    </AccordionDetails>
  );
};

function getLabel(status: SyncJobStatus): string {
  switch (status) {
    case SYNC_JOB_STATUSES.IN_PROGRESS:
    case SYNC_JOB_STATUSES.PENDING:
      return 'QuickBooks not synced';
    case SYNC_JOB_STATUSES.FAILED:
      return 'Sync failed';
    case SYNC_JOB_STATUSES.COMPLETED:
      return 'Synced to QuickBooks';
    default:
      return raiseUnreachable(status);
  }
}

function getIcon(status: SyncJobStatus): VyIconType {
  switch (status) {
    case SYNC_JOB_STATUSES.IN_PROGRESS:
    case SYNC_JOB_STATUSES.PENDING:
      return 'QuickbooksNotSynced';
    case SYNC_JOB_STATUSES.FAILED:
      return 'QuickbooksSyncFailed';
    case SYNC_JOB_STATUSES.COMPLETED:
      return 'QuickbooksSynced';
    default:
      return raiseUnreachable(status);
  }
}
